import * as MemoizeUtils from "sh-application/utility/MemoizeUtils";
import * as moment from "moment";
import DateUtils from "sh-application/utility/DateUtils";
import StringsStore from "sh-strings/store";
import TimeZoneUtils from "sh-application/utility/TimeZoneUtils";
import type { Moment } from "moment";
import { ScheduleCalendarType, ScheduleCalendarTypes } from "sh-models";

/**
 * Input type for DateTimeFormatter methods.
 * Accepts both native Date (preferred for new code) and Moment (backward-compatible).
 */
export type DateTimeInput = Date | Moment;

/**
 * Date time format types.
 *
 * Note:
 * Please keep the number of date time formats down to a minimum and reuse existing ones as much as possible.
 * Introducing new date time formats incurs costs in code and localization complexity that we should avoid.
 */
export enum DateTimeFormatType {
    // Time only
    Time,                               // 8:00 PM, 8:30 AM, 23:00
    Time_Short,                         // 8 PM, 8:30 AM, 23:00
    Time_HourOnly,                      // 8, 8, 23
    Time_MeridiemOnly,                  // PM, AM, "" (empty string if 24-hour format is being used)

    // Date only
    Date_Numeric,                       // 12/27/1985
    Date_DateMonthNumeric,              // 12/27
    Date_DateMonthShort,                // Dec 27
    Date_DateMonthLong,                 // December 27
    Date_MonthYearLong,                 // December 1985
    Date_MonthLong,                     // December
    Date_DateMonthYearShort,            // Aug 1, 2021
    Date_DateMonthYearLong,             // December 27, 1985
    Date_DayDateMonthYear,              // Wednesday, Dec 27, 1985
    Date_DayShortDateMonthYear,         // Wed, Dec 25, 1990
    Date_DayDateMonthYearLong,          // Wednesday, December 27, 1985
    Date_DayDateMonthShort,             // Wed Dec 27
    Date_DateNumeric,                   // 27
    Date_DateNumericPadded,             // 07
    Date_YearNumeric,                   // 1985
    Date_Day,                           // Wednesday
    Date_DayShort,                      // Wed
    Date_DayNarrow,                     // We

    // Date and time
    DateTime_DateNumeric_Time,          // 12/27 8 PM
    DateTime_DateShort_Time,            // Dec 27, 8 PM
    DateTime_DateYearShort_Time         // Aug 1, 2021, 8 PM
}

// Callback type for formatting a single day all day date time range
type GetAllDayDateTimeAsStringFunction = (dateTime: Moment, useLongFormat: boolean) => string;

// Callback for formatting same day date time ranges as strings
type GetSameDayDateTimeRangeAsStringFunction = (startDate: Moment, endDate: Moment, useLongFormat: boolean) => string;

const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;

/**
 * DateTimeFormatter
 * Utility class for generating localized, formatted date time strings.
 */
export default class DateTimeFormatter {
    // TODO DCoh:  Change this class to a singleton instead of static, and also have this be derived from a base interface to support better testability.

    private static _dateTimeStrings: Map<string, string> = null;
    private static _commonStrings: Map<string, string> = null;
    private static _currentLocale: string = null;
    private static _isCurrentLocale12HourTime = false;
    private static _useLeadingZeroHour = false;
    private static _hasRegionalTimeOverride = false;

    // Pre-computed Intl options, set once at initialize() time.
    private static _regionalOverrideOptions: Partial<Intl.DateTimeFormatOptions> = {};
    // Regex to strip locale-specific leading zero from formatted hour, pre-computed at init time.
    private static _leadingZeroRegex: RegExp = null;

    private static _hasRegionalDateOverride = false;
    private static _shortDateDayFormat: "2-digit" | "numeric" = "numeric";
    private static _shortDateMonthFormat: "2-digit" | "numeric" = "numeric";
    private static _assembleShortDate: (parts: Intl.DateTimeFormatPart[]) => string = null;
    private static _assembleShortDateWithYear: (parts: Intl.DateTimeFormatPart[]) => string = null;
    // Component order (e.g., ["day","month","year"]) and separator — used by parseBrowserDate and formatBrowserDate.
    private static _shortDateOrder: string[] = null;
    private static _shortDateSeparator: string = "/";
    // Cached formatter for formatBrowserDate (no timezone).
    private static _numericDateFmtNoTz: Intl.DateTimeFormat | null = null;

    // Formatter caches — keyed by (timezone + locale + regional settings), auto-rebuild when any changes.
    private static _hourOnlyFmtCache: { key: string; fmt: Intl.DateTimeFormat } | null = null;
    private static _meridiemFmtCache: { key: string; fmt: Intl.DateTimeFormat } | null = null;
    private static _shortDateFmtCache: { key: string; fmt: Intl.DateTimeFormat; fmtWithYear: Intl.DateTimeFormat } | null = null;

    /** Cache key combining timezone, locale, and regional settings. */
    private static get _fmtCacheKey(): string {
        return `${TimeZoneUtils.displayTimezone}|${DateTimeFormatter._currentLocale}|${DateTimeFormatter._hasRegionalTimeOverride}|${DateTimeFormatter._isCurrentLocale12HourTime}|${DateTimeFormatter._useLeadingZeroHour}`;
    }

    /** Get hour-only formatter, rebuilding if settings changed. */
    private static getHourOnlyFormatter(): Intl.DateTimeFormat {
        const key = DateTimeFormatter._fmtCacheKey;
        if (!DateTimeFormatter._hourOnlyFmtCache || DateTimeFormatter._hourOnlyFmtCache.key !== key) {
            const tz = TimeZoneUtils.displayTimezone;
            DateTimeFormatter._hourOnlyFmtCache = {
                key,
                fmt: new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, {
                    hour: "numeric",
                    ...DateTimeFormatter._regionalOverrideOptions,
                    ...(tz ? { timeZone: tz } : {})
                })
            };
        }
        return DateTimeFormatter._hourOnlyFmtCache.fmt;
    }

    /** Get meridiem formatter, rebuilding if settings changed. */
    private static getMeridiemFormatter(): Intl.DateTimeFormat {
        const key = DateTimeFormatter._fmtCacheKey;
        if (!DateTimeFormatter._meridiemFmtCache || DateTimeFormatter._meridiemFmtCache.key !== key) {
            const tz = TimeZoneUtils.displayTimezone;
            DateTimeFormatter._meridiemFmtCache = {
                key,
                fmt: new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, {
                    hour: "numeric", hour12: true,
                    ...(tz ? { timeZone: tz } : {})
                })
            };
        }
        return DateTimeFormatter._meridiemFmtCache.fmt;
    }

    /** Get short date formatter, rebuilding if settings changed. */
    private static getShortDateFormatter(): Intl.DateTimeFormat {
        const key = DateTimeFormatter._fmtCacheKey;
        if (!DateTimeFormatter._shortDateFmtCache || DateTimeFormatter._shortDateFmtCache.key !== key) {
            const tz = TimeZoneUtils.displayTimezone;
            const baseOpts: Intl.DateTimeFormatOptions = {
                day: DateTimeFormatter._shortDateDayFormat,
                month: DateTimeFormatter._shortDateMonthFormat,
                ...(tz ? { timeZone: tz } : {})
            };
            DateTimeFormatter._shortDateFmtCache = {
                key,
                fmt: new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, baseOpts),
                fmtWithYear: new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, { ...baseOpts, year: "numeric" })
            };
        }
        return DateTimeFormatter._shortDateFmtCache.fmt;
    }

    /** Get short date with year formatter. */
    private static getShortDateWithYearFormatter(): Intl.DateTimeFormat {
        if (!DateTimeFormatter._shortDateFmtCache || DateTimeFormatter._shortDateFmtCache.key !== DateTimeFormatter._fmtCacheKey) {
            DateTimeFormatter.getShortDateFormatter(); // builds both
        }
        return DateTimeFormatter._shortDateFmtCache.fmtWithYear;
    }

    /**
     * Initialize DateTimeFormatter module.
     * @param {string} currentLocale - current locale
     * @param {string} osShortTimeFormat - optional OS short time format pattern from Teams regional settings
     *        (e.g., "h:mm tt" for 12-hour, "HH:mm" for 24-hour). When provided, this takes priority
     *        over locale-based detection for determining 12/24-hour time format.
     * @param {string} osShortDateFormat - optional OS short date format pattern from Teams regional settings
     *        (e.g., "dd/MM/yyyy" for day-first, "MM/dd/yyyy" for month-first, "yyyy-MM-dd" for ISO).
     *        When provided, overrides component order, separator, and leading zeros for numeric date formats.
     */
    public static initialize(currentLocale: string, osShortTimeFormat?: string, osShortDateFormat?: string): void {
        DateTimeFormatter._currentLocale = currentLocale;

        DateTimeFormatter._dateTimeStrings = StringsStore().registeredStringModules.get("dateTimeFormats").strings;
        DateTimeFormatter._commonStrings = StringsStore().registeredStringModules.get("common").strings;

        // Determine 12/24-hour preference.
        // Priority 1: OS regional settings from Teams SDK (user's explicit preference)
        // Priority 2: Locale-based detection via Intl.DateTimeFormat
        if (osShortTimeFormat) {
            DateTimeFormatter._isCurrentLocale12HourTime = !osShortTimeFormat.includes("H");
            DateTimeFormatter._useLeadingZeroHour = /[hH]{2}/.test(osShortTimeFormat);
            DateTimeFormatter._hasRegionalTimeOverride = true;
        } else {
            DateTimeFormatter._hasRegionalTimeOverride = false;
            DateTimeFormatter._useLeadingZeroHour = false;
            const resolvedOptions = new Intl.DateTimeFormat(currentLocale, { timeZone: 'UTC', hour: 'numeric' }).resolvedOptions();
            DateTimeFormatter._isCurrentLocale12HourTime = resolvedOptions?.hour12 ?? false;
        }

        // Pre-compute Intl options and cached formatters for regional overrides
        DateTimeFormatter._regionalOverrideOptions = DateTimeFormatter._hasRegionalTimeOverride
            ? { hour12: DateTimeFormatter._isCurrentLocale12HourTime, ...(DateTimeFormatter._useLeadingZeroHour ? { hour: "2-digit" as const } : {}) }
            : {};
        // Formatters are lazily built on first use (getHourOnlyFormatter, etc.)
        // and rebuilt when timezone changes (clearFormattingCaches nulls them out).

        // Pre-compute regex for stripping locale-specific leading zero from formatted hours.
        // Uses Intl.NumberFormat to get the locale's zero digit (e.g., "0" for Latin, "٠" for Arabic-Indic).
        const zeroDigit = new Intl.NumberFormat(currentLocale, { useGrouping: false }).format(0);
        const escapedZero = zeroDigit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        DateTimeFormatter._leadingZeroRegex = new RegExp(`^(\\s|\\u200e|\\u200f)*${escapedZero}`);

        // Determine numeric date format preferences from OS regional settings.
        // Detects component order (DMY/MDY/YMD), separator, and leading zeros.
        const dayPos = osShortDateFormat ? osShortDateFormat.search(/d/i) : -1;
        const monthPos = osShortDateFormat ? osShortDateFormat.search(/M/) : -1;
        const yearPos = osShortDateFormat ? osShortDateFormat.search(/y/i) : -1;
        // Only enable override if the pattern contains at least day and month tokens
        if (osShortDateFormat && dayPos !== -1 && monthPos !== -1) {
            DateTimeFormatter._hasRegionalDateOverride = true;

            const dayFormat: "2-digit" | "numeric" = /dd/.test(osShortDateFormat) ? "2-digit" : "numeric";
            const monthFormat: "2-digit" | "numeric" = /MM/.test(osShortDateFormat) ? "2-digit" : "numeric";

            // Extract separator: first character between tokens (skip leading letters)
            const separatorMatch = osShortDateFormat.match(/[a-zA-Z]([^a-zA-Z])[a-zA-Z]/);
            const separator = separatorMatch ? separatorMatch[1] : "/";

            // Build component order, excluding any missing tokens
            const components = [
                { pos: dayPos, type: "day" },
                { pos: monthPos, type: "month" },
                ...(yearPos !== -1 ? [{ pos: yearPos, type: "year" }] : [])
            ].sort((a, b) => a.pos - b.pos);
            const orderWithYear = components.map(c => c.type);
            const orderWithoutYear = orderWithYear.filter(t => t !== "year");

            // Store format config for lazy formatter creation (getShortDateFormatter)
            DateTimeFormatter._shortDateDayFormat = dayFormat;
            DateTimeFormatter._shortDateMonthFormat = monthFormat;
            DateTimeFormatter._shortDateFmtCache = null; // built lazily on first use

            const assembleDateParts = (parts: Intl.DateTimeFormatPart[], order: string[]) => {
                const values: Record<string, string> = {};
                for (const p of parts) {
                    if (p.type === "day" || p.type === "month" || p.type === "year") {
                        values[p.type] = p.value;
                    }
                }
                return order.map(t => values[t]).join(separator);
            };
            DateTimeFormatter._assembleShortDate = (parts) => assembleDateParts(parts, orderWithoutYear);
            DateTimeFormatter._assembleShortDateWithYear = (parts) => assembleDateParts(parts, orderWithYear);
            DateTimeFormatter._shortDateOrder = orderWithYear;
            DateTimeFormatter._shortDateSeparator = separator;
        } else {
            DateTimeFormatter._hasRegionalDateOverride = false;
            DateTimeFormatter._shortDateFmtCache = null;
            DateTimeFormatter._assembleShortDate = null;
            DateTimeFormatter._assembleShortDateWithYear = null;
            // Detect locale-default component order for parseBrowserDate fallback.
            const probe = new Date(2000, 11, 25); // Dec 25, 2000
            const probeStr = probe.toLocaleDateString(currentLocale, { day: "numeric", month: "numeric", year: "numeric" });
            const probeParts = probeStr.split(/[\/\-\.]/);
            const probeSepMatch = probeStr.match(/\d([\/\-\.])\d/);
            DateTimeFormatter._shortDateSeparator = probeSepMatch ? probeSepMatch[1] : "/";
            DateTimeFormatter._shortDateOrder = probeParts.map(p => {
                const n = parseInt(p, 10);
                if (n === 25) { return "day"; }
                if (n === 12) { return "month"; }
                if (n === 2000) { return "year"; }
                return "unknown";
            });
        }
        DateTimeFormatter._numericDateFmtNoTz = null; // invalidate on any config change

        // Formatter caches are keyed by timezone and auto-rebuild — no explicit clearing needed.
        // Event range string cache includes timezone in key, so stale entries won't match.
    }

    /**
     * Generates a formatted string for the specified date/time.
     * Accepts both native Date and Moment inputs. All formatting uses Intl.DateTimeFormat.
     *
     * @param {DateTimeInput} dateTime - date/time to format (Date or Moment)
     * @param {DateTimeFormatType} dateTimeFormatType - format type to use
     * @returns {string} formatted date time string
     */
    public static getDateTimeAsString(dateTime: DateTimeInput, dateTimeFormatType: DateTimeFormatType): string {
        if (!dateTime) {
            return "";
        }

        let result: string = "";

        // Javascript toLocaleString options:
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
        // Set this value to use Javascript toLocaleString for date time formatting.
        let localeStringOptions: Intl.DateTimeFormatOptions = null;

        // Note:
        // Please keep the number of date time formats down to a minimum and reuse existing ones as much as possible.
        // Introducing new date time formats incurs costs in code and localization complexity that we should avoid.

        // Compute format string or result for the specified format type
        switch (dateTimeFormatType) {
            // Time formats use Intl via formatTime/formatHourOnly/formatMeridiemOnly.
            // Date formats use Intl via toLocaleString with localeStringOptions.
            // Day names use Intl via formatWeekday.

            case DateTimeFormatType.Time:  // 8:00 PM / 14:00
                result = DateTimeFormatter.formatTime(dateTime, false /* useShortFormat */);
                break;
            case DateTimeFormatType.Time_Short:  // 8 PM / 14:00 (omits :00 minutes for 12-hour locales)
                result = DateTimeFormatter.formatTime(dateTime, true /* useShortFormat */);
                break;
            case DateTimeFormatType.Time_HourOnly:  // 8 / 14
                result = DateTimeFormatter.formatHourOnly(dateTime);
                break;
            case DateTimeFormatType.Time_MeridiemOnly:  // PM / "" (empty for 24-hour locales)
                result = DateTimeFormatter.formatMeridiemOnly(dateTime);
                break;
            case DateTimeFormatType.Date_Numeric:  // 12/27/1985
                if (DateTimeFormatter._hasRegionalDateOverride) {
                    const date = DateTimeFormatter.toNativeDate(dateTime);
                    if (date) {
                        result = DateTimeFormatter._assembleShortDateWithYear(
                            DateTimeFormatter.getShortDateWithYearFormatter().formatToParts(date)
                        );
                        break;
                    }
                }
                // Fall back to locale defaults when no override or toDisplayDate() returned null
                localeStringOptions = { month: "numeric", day: "numeric", year: "numeric" };
                break;
            case DateTimeFormatType.Date_DateMonthNumeric:  // 12/27
                if (DateTimeFormatter._hasRegionalDateOverride) {
                    const date = DateTimeFormatter.toNativeDate(dateTime);
                    if (date) {
                        result = DateTimeFormatter._assembleShortDate(
                            DateTimeFormatter.getShortDateFormatter().formatToParts(date)
                        );
                        break;
                    }
                }
                // Fall back to locale defaults when no override or toDisplayDate() returned null
                localeStringOptions = { month: "numeric", day: "numeric" };
                break;
            case DateTimeFormatType.Date_DateMonthShort:  //  Dec 27
                localeStringOptions = { month: "short", day: "numeric" };
                break;
            case DateTimeFormatType.Date_DateMonthLong:  // December 27
                localeStringOptions = { month: "long", day: "numeric" };
                break;
            case DateTimeFormatType.Date_MonthYearLong:  // December 1985
                localeStringOptions = { year: "numeric", month: "long" };
                break;
            case DateTimeFormatType.Date_MonthLong:      // December
                localeStringOptions = { month: "long" };
                break;
            case DateTimeFormatType.Date_DayDateMonthYear:  // Wednesday, Dec 27, 1985
                localeStringOptions = { year: "numeric", month: "short", day: "numeric", weekday: "long" };
                break;
            case DateTimeFormatType.Date_DayShortDateMonthYear:  // Wed, Dec 27, 1985
                localeStringOptions = { year: "numeric", month: "short", day: "numeric", weekday: "short" };
                break;
            case DateTimeFormatType.Date_DayDateMonthYearLong:  // Wednesday, December 27, 1985
                localeStringOptions = { year: "numeric", month: "long", day: "numeric", weekday: "long" };
                break;
            case DateTimeFormatType.Date_DayDateMonthShort:  // Wed Dec 27
                localeStringOptions = { month: "short", day: "numeric", weekday: "short" };
                break;
            case DateTimeFormatType.Date_DateMonthYearShort:  // Aug 1, 2021
                localeStringOptions = { year: "numeric", month: "short", day: "numeric" };
                break;
            case DateTimeFormatType.Date_DateMonthYearLong:  // December 27, 1985
                localeStringOptions = { year: "numeric", month: "long", day: "numeric" };
                break;
            case DateTimeFormatType.Date_DateNumeric:  // 27
                localeStringOptions = { day: "numeric" };
                break;
            case DateTimeFormatType.Date_DateNumericPadded:  // 07
                localeStringOptions = { day: "2-digit" };
                break;
            case DateTimeFormatType.Date_YearNumeric:  // 1985
                localeStringOptions = { year: "numeric" };
                break;
            case DateTimeFormatType.Date_Day:  // Wednesday
            case DateTimeFormatType.Date_DayShort:  // Wed
            case DateTimeFormatType.Date_DayNarrow:  // We
                result = DateTimeFormatter.formatWeekday(dateTime, dateTimeFormatType);
                break;
            case DateTimeFormatType.DateTime_DateNumeric_Time:  // 12/27 8 PM
                result = DateTimeFormatter.constructDateTimeString(dateTime, DateTimeFormatType.Date_DateMonthNumeric, DateTimeFormatType.Time_Short);
                break;
            case DateTimeFormatType.DateTime_DateShort_Time:  // Dec 27, 8 PM
                result = DateTimeFormatter.constructDateTimeString(dateTime, DateTimeFormatType.Date_DateMonthShort, DateTimeFormatType.Time_Short, true /* useComma */);
                break;
            case DateTimeFormatType.DateTime_DateYearShort_Time:  // Aug 1, 2021, 8 PM
                result = DateTimeFormatter.constructDateTimeString(dateTime, DateTimeFormatType.Date_DateMonthYearShort, DateTimeFormatType.Time_Short, true /* useComma */);
                break;
        }

        try {
            if (localeStringOptions) {
                const date = DateTimeFormatter.toNativeDate(dateTime);
                if (date) {
                    const tz = DateTimeFormatter.resolveTimezone(dateTime);
                    result = date.toLocaleString(
                        DateTimeFormatter._currentLocale,
                        { ...localeStringOptions, ...(tz ? { timeZone: tz } : {}) }
                    );
                }
            }
        } catch (error) {
            console.error(error);
            result = "";
        }
        // Otherwise the result should have been assigned a formatted date time string

        return result;
    }

    /**
     * Returns true if the current locale is on 12-hour time. false if the 24-hour time.
     */
    public static isCurrentLocale12HourTime(): boolean {
        return DateTimeFormatter._isCurrentLocale12HourTime;
    }

    /**
     * Generates a numeric string for the day of the month from a given date in English
     * @param {DateTimeInput} date - The date/time to extract the day from.
     * @returns {string} numeric day string. (1, 2, 3 ... 31)
     */
    public static getNumericDateString(date: DateTimeInput): string {
        if (!date) {
            return "";
        }
        const tz = DateTimeFormatter.resolveTimezone(date);
        const options: Intl.DateTimeFormatOptions = { day: 'numeric', ...(tz ? { timeZone: tz } : {}) };
        const nativeDate = DateTimeFormatter.toNativeDate(date);
        // not all locale return a numeric string.
        return nativeDate.toLocaleDateString('en-US', options);
    }

    /**
     * Generates a formatted date time range string for events that may be multi-day and thus need date info to be
     * included along with the times (eg, timeoffs, shift requests, etc)
     * @param {Moment} startTime
     * @param {Moment} endTime
     * @param {boolean} useLongFormat - set to true to generate the longer/more verbose version of the string
     * @param {GetAllDayDateTimeAsStringFunction} getAllDayDateTimeAsStringFunction
     * @param {GetSameDayDateTimeRangeAsStringFunction} getSameDayDateTimeRangeAsStringFunction
     * @returns {string} formatted date time range string
     */
    private static generateEventDateTimeRangeAsString(
        startTime: Moment,
        endTime: Moment,
        useLongFormat: boolean,
        getAllDayDateTimeAsStringFunction: GetAllDayDateTimeAsStringFunction,
        getSameDayDateTimeRangeAsStringFunction: GetSameDayDateTimeRangeAsStringFunction
    ) {
        let rangeString = "";
        const isAllDayRange = DateUtils.isAllDayTimeRange(startTime, endTime);
        const isSameDay = DateUtils.isTimeRangeForTheSameDay(startTime, endTime);
        if (isAllDayRange) {
            if (isSameDay) {
                // If the range just represents a single full day, then represent the range as a single all day event.
                rangeString = getAllDayDateTimeAsStringFunction(startTime, useLongFormat);
            } else {
                // Get the actual last inclusive day for the date range.
                // If the All Day range ends at the beginning of the next day (ie, midnight), then
                // we want to use the previous day for displaying the end date.
                // eg, "5/1 12am - 5/3 12am" should be rendered as "5/1 - 5/2"
                let lastDay = DateUtils.getLastDayForEndDate(endTime);

                const dateOnlyFormatType: DateTimeFormatType = useLongFormat ? DateTimeFormatType.Date_DateMonthShort : DateTimeFormatType.Date_DateMonthNumeric;
                rangeString = DateTimeFormatter.getDateTimeRangeAsString(startTime, lastDay, dateOnlyFormatType);
            }
        } else {
            let dateTimeFormatType: DateTimeFormatType;
            if (isSameDay) {
                rangeString = getSameDayDateTimeRangeAsStringFunction(startTime, endTime, useLongFormat);
            } else {
                // Otherwise display the dates + times
                dateTimeFormatType = useLongFormat ? DateTimeFormatType.DateTime_DateShort_Time : DateTimeFormatType.DateTime_DateNumeric_Time;
                rangeString = DateTimeFormatter.getDateTimeRangeAsString(startTime, endTime, dateTimeFormatType);
            }
        }
        return rangeString;
    }

    /**
     * Helper for computing the string for when a shift runs through all of a single day
     */
    private static getAllDayDateTimeAsStringForShift(dateTime: Moment, useLongFormat: boolean): string {
        return useLongFormat ? DateTimeFormatter.getDateTimeAsString(dateTime, DateTimeFormatType.Date_DateMonthShort) : DateTimeFormatter.getString("allDayEvent");
    }

    /**
     * Helper for computing the time range for when a shift starts and ends on the same day
     */
    private static getSameDayDateTimeRangeAsStringForShift(startDate: Moment, endDate: Moment, useLongFormat: boolean): string {
        return DateTimeFormatter.getDateTimeRangeAsString(startDate, endDate, DateTimeFormatType.Time_Short);
    }

    /**
     * Generates a formatted date time range string for events that need both date and time info to be rendered (eg, multi-day events such as timeoffs)
     * @param {Moment} startTime
     * @param {Moment} endTime
     * @param {boolean} useLongFormat - set to true to generate the longer/more verbose version of the string
     * @returns {string} formatted date time range string
     */
    public static getEventDateTimeRangeAsString(startTime: Moment, endTime: Moment, useLongFormat?: boolean): string {
        return DateTimeFormatter.generateEventDateTimeRangeAsString(
            startTime,
            endTime,
            useLongFormat,
            DateTimeFormatter.getAllDayDateTimeAsStringForShift,
            useLongFormat
                ? DateTimeFormatter.getSameDayDateTimeRangeAsStringForShiftRequest
                : DateTimeFormatter.getSameDayDateTimeRangeAsStringForShift
        );
    }

    private static _eventDateTimeRangeStringCache: {[key: string]: string} = {};

    public static fastGetEventDateTimeRangeAsString = MemoizeUtils.memoizeUtility(
        DateTimeFormatter.getEventDateTimeRangeAsString,
        DateTimeFormatter._eventDateTimeRangeStringCache,
        (args: any[]) => {
            return `${args[0].valueOf()}:${args[1].valueOf()}:${!!args[2]}:${TimeZoneUtils.displayTimezone}`;
        }
    );

    /**
     * Generate a formatted time range string for events that don't need date info and only need the time info to be rendered
     * (eg, shifts, which are only allowed to span a day)
     * Only the times are rendered, and date info is not included, even if the event spans into the
     * next day.
     * @param {Moment} startTime start time
     * @param {Moment} endTime end time
     * @returns {string} time range string
     */
    public static getEventTimeRangeAsString(startTime: Moment, endTime: Moment) {
        return DateTimeFormatter.getDateTimeRangeAsString(startTime, endTime, DateTimeFormatType.Time_Short);
    }

    /**
     * Generate the date range string for the Schedule header
     * @param currentSelectedDate currently selected date in the schedule
     * @param viewStartDate start date for the current schedule view
     * @param viewEndDate end date for the current schedule view
     * @param scheduleType schedule view type (eg, week, month, day)
     * @param isForAriaLabel pass true if this is for an aria label and the output will be a bit more verbose for screen readers
     */
    public static getScheduleHeaderDateRangeAsString(currentSelectedDate: Moment, viewStartDate: Moment, viewEndDate: Moment, scheduleType: ScheduleCalendarType, isForAriaLabel: boolean = false): string {
        let result: string = "";
        switch (scheduleType) {
            case ScheduleCalendarTypes.Day:
                // Day view: display the date of the currentl selected day
                result = DateTimeFormatter.getDateTimeAsString(currentSelectedDate, DateTimeFormatType.Date_DayDateMonthYear);
                break;
            case ScheduleCalendarTypes.Month:
                // Month view: display the month for the current selected date
                result = DateTimeFormatter.getDateTimeAsString(currentSelectedDate, DateTimeFormatType.Date_MonthYearLong);
                break;
            case ScheduleCalendarTypes.Fortnight:
            case ScheduleCalendarTypes.Week:
            default:
                // Week/Fortnight view and other date ranges: display the start and end dates for the range
                result = DateTimeFormatter.getDateRangeLongAsString(viewStartDate, viewEndDate, isForAriaLabel);
                break;
        }
        return result;
    }

    /**
     * Generates a date range in long format, which includes month, day and year.
     * If the year/month/day is the same for both start and end dates, then that component is only mentioned once instead of repeated for both start and end dates.
     * Ex: June 25 - Aug 1, 2017 (Different months, same year)
     * Ex: July 2 - 8, 2017 (Same month, same year)
     * Ex: December 25, 2017 - January 2, 2018 (Different years)
     * @param startDate start date for the range
     * @param endDate end date for the range
     * @param isForAriaLabel pass true if this is for an aria label and the output will be a bit more verbose for screen readers
     */
    public static getDateRangeLongAsString(startDate: Moment, endDate: Moment, isForAriaLabel: boolean = false): string {
        let result: string = "";
        if (startDate && endDate) {
            if (startDate.year() !== endDate.year()) {
                // Different years, so use the full date format for both start and end dates
                const startDateString: string = DateTimeFormatter.getDateTimeAsString(startDate, DateTimeFormatType.Date_DateMonthYearLong);
                const endDateString: string = DateTimeFormatter.getDateTimeAsString(endDate, DateTimeFormatType.Date_DateMonthYearLong);
                result = DateTimeFormatter.constructDateTimeRangeString(startDateString, endDateString);
            } else {
                // Same year, so only include the year once
                const yearString: string = DateTimeFormatter.getDateTimeAsString(startDate, DateTimeFormatType.Date_YearNumeric);
                if (!isForAriaLabel && startDate.month() === endDate.month()) {
                    // Same month, so only include the month once for the start date
                    const monthString: string = DateTimeFormatter.getDateTimeAsString(startDate, DateTimeFormatType.Date_MonthLong);
                    const startDateString: string = DateTimeFormatter.getDateTimeAsString(startDate, DateTimeFormatType.Date_DateNumeric);
                    const endDateString: string = DateTimeFormatter.getDateTimeAsString(endDate, DateTimeFormatType.Date_DateNumeric);
                    result = DateTimeFormatter.getString("dateTimeRangeFormatWithSameYearAndMonth").format(monthString, startDateString, endDateString, yearString);
                } else {
                    // Different months, so include the months for both start and end dates
                    const startDateString: string = DateTimeFormatter.getDateTimeAsString(startDate, DateTimeFormatType.Date_DateMonthLong);
                    const endDateString: string = DateTimeFormatter.getDateTimeAsString(endDate, DateTimeFormatType.Date_DateMonthLong);
                    result = DateTimeFormatter.getString("dateTimeRangeFormatWithSameYear").format(startDateString, endDateString, yearString);
                }
            }
        }
        return result;
    }

    /**
     * Helper for computing the time string when a shift request is for all day of a single date
     */
    private static getAllDayDateTimeAsStringForShiftRequest(dateTime: Moment, useLongFormat: boolean): string {
        const dateString: string = DateTimeFormatter.getDateTimeAsString(dateTime, DateTimeFormatType.Date_DateMonthShort);
        return DateTimeFormatter.getString("allDayEventWithDateFormat").format(dateString);
    }

    /**
     * Helper for computing the time range of a shift request when the shift request starts and ends on same day
     * Ex: Mar 14, 10 AM - 4:30 PM
     */
    private static getSameDayDateTimeRangeAsStringForShiftRequest(startDate: Moment, endDate: Moment, useLongFormat: boolean): string {
        const startDateFormat: DateTimeFormatType = useLongFormat ? DateTimeFormatType.DateTime_DateShort_Time : DateTimeFormatType.DateTime_DateNumeric_Time;
        return DateTimeFormatter.getDateTimeRangeAsString(startDate, endDate, startDateFormat, "" /* emptyTimeString */, DateTimeFormatType.Time_Short);
    }

    /**
     * Generates a formatted date time range string for shift requests
     */
    public static getShiftRequestDateTimeRangeAsString(startTime: Moment, endTime: Moment): string {
        return DateTimeFormatter.generateEventDateTimeRangeAsString(startTime, endTime, true /* useLongFormat */,
            DateTimeFormatter.getAllDayDateTimeAsStringForShiftRequest, DateTimeFormatter.getSameDayDateTimeRangeAsStringForShiftRequest);
    }

    /**
     * Constructs a formatted date time range string using the specified format type
     * @param {Moment} startTime
     * @param {Moment} endTime
     * @param {DateTimeFormatType} formatType - date time format type
     * @param {string} emptyTimeString - alternate string to use if a specified time is null
     * @param {DateTimeFormatType} endFormatType - Optionally set a different endFormatType than the start format type
     * @returns {string} formatted date time range
     */
    public static getDateTimeRangeAsString(startTime: DateTimeInput, endTime: DateTimeInput, formatType: DateTimeFormatType, emptyTimeString: string = "", endFormatType: DateTimeFormatType = formatType): string {
        const startTimeString: string = startTime ? DateTimeFormatter.getDateTimeAsString(startTime, formatType) : emptyTimeString;
        const endTimeString: string = endTime ? DateTimeFormatter.getDateTimeAsString(endTime, endFormatType) : emptyTimeString;
        return DateTimeFormatter.constructDateTimeRangeString(startTimeString, endTimeString);
    }

     /**
     * Use a short format for a time that returns the day of the week.
     * Any Day: Thursday
     * @param date
     */
     public static getDisplayTimeInWeekDayFormat(date: Moment): string {
        const displayTime = DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Date_Day);
        return displayTime;
    }

     /**
     * Use a short format for a time that returns the time of the day.
     * Any Day: Thursday
     * @param date
     */
     public static getDisplayTimeInTimeFormat(date: Moment): string {
        const displayTime = DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Time);
        return displayTime;
    }

    /**
     * Use a short format for a time that is different depending on how far the time is from now.
     * Same Day: 2:30 AM
     * Same Week: Thursday
     * Else: May 4
     * @param date
     */
    public static getDisplayTimeInRelativeFormat(date: Moment): string {
        let displayTime: string = "";
        const currentTime: Moment = moment();
        if (date) {
            if (currentTime.isSame(date, 'day')) {
                displayTime = DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Time);
            } else if (currentTime.isSame(date, 'week')) {
                displayTime = DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Date_Day);
            } else {
                displayTime = DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Date_DateMonthShort);
            }
        }
        return displayTime;
    }

    /**
     * Use a long format for a time that is different depending on how far the time is from now.
     * Same Day: Today 2:30 AM
     * Same Week: Thursday 2:30 AM
     * Else: May 4, 2:30 AM
     * @param date
     */
    public static getDisplayTimeInRelativeLongFormat(dateTime: Moment): string {
        let displayTime: string = "";
        const currentTime: Moment = moment();
        if (dateTime) {
            if (currentTime.isSame(dateTime, 'day')) {
                displayTime = DateTimeFormatter.getString("todayTimeFormat").format(DateTimeFormatter.getDateTimeAsString(dateTime, DateTimeFormatType.Time_Short));
            } else if (currentTime.isSame(dateTime, 'week')) {
                displayTime = DateTimeFormatter.constructDateTimeString(dateTime, DateTimeFormatType.Date_DayShort, DateTimeFormatType.Time_Short, true /* useComma */);
            } else {
                displayTime = DateTimeFormatter.getDateTimeAsString(dateTime, DateTimeFormatType.DateTime_DateShort_Time);
            }
        }
        return displayTime;
    }

    /**
     * It generate time duration string in hours and minutes ( For example: 7 hours 30 minutes / 7 hours / 30 minutes )
     * It calculates time duration  between start time and end time
     * If start and end time gap is 0 then timeDuration return undefined.
     */
    public static getTimeDurationForDisplay(startTime: Moment, endTime: Moment): string {
        let timeDuration: string;
        const hours: number = endTime.diff(startTime, 'hours' , false /** precise */);
        const minutes: number = DateUtils.getDifferenceInMinutesFromMoments(startTime, endTime, false /** precise */) % MINUTES_IN_HOUR;
        const hourStr: string = hours === 1 ? DateTimeFormatter.getString("oneHour") : DateTimeFormatter.getString("numHours").format(hours.toString());
        const minuteStr: string = DateTimeFormatter.getString("numMinutes").format(minutes.toString());

        if (hours && minutes) {
            timeDuration = DateTimeFormatter.getString("timeDuration").format(hourStr, minuteStr);
        } else if (hours) {
            timeDuration = hourStr;
        } else if (minutes) {
            timeDuration = minuteStr;
        }

        return timeDuration;
    }

     /**
     * It generate date time duration string in days, hours and minutes ( For example: 2d 3h / 2 days 3 hours, 7h 30m / 7 hours / 30 minutes )
     * It calculates time duration  between start time and end time
     * If start and end time gap is 0 then timeDuration return undefined.
     * @param startTime start time of the duration
     * @param endTime end time of the duration
     * @param displayShort set true if short display format is required
     */
    public static getDateTimeDurationForDisplay(startTime: Moment, endTime: Moment, displayShort?: boolean, isAllDay?: boolean): string {
        let timeDuration: string;
        const daysDurationFormat: string = displayShort ? "daysDurationShort" : "numDays";
        const hoursDurationFormat: string = displayShort ? "hoursDurationShort" : "numHours";
        const minutesDurationFormat: string = displayShort ? "minutesDurationShort" : "numMinutes";

        if (DateUtils.isAllDayTimeRange(startTime, endTime) || isAllDay) {
            const days: number = Math.ceil(DateUtils.getDifferenceInDaysFromMoments(startTime, endTime));
            const dayStr: string = DateTimeFormatter.getString(daysDurationFormat).format(days.toString());
            timeDuration = dayStr;
        } else {
            const minutes: number = DateUtils.getDifferenceInMinutesFromMoments(startTime, endTime, false) % MINUTES_IN_HOUR;
            const hours: number = DateUtils.getDifferenceInHoursFromMoments(startTime, endTime, false) % HOURS_IN_DAY;
            const days: number = DateUtils.getDifferenceInDaysFromMoments(startTime, endTime, false);
            const dayStr: string = DateTimeFormatter.getString(daysDurationFormat).format(days.toString());
            const hourStr: string = DateTimeFormatter.getString(hoursDurationFormat).format(hours.toString());
            const minuteStr: string = DateTimeFormatter.getString(minutesDurationFormat).format(minutes.toString());

            if (days && hours) {
                timeDuration = DateTimeFormatter.getString("timeDuration").format(dayStr, hourStr);
            } else if (hours && minutes) {
                timeDuration = DateTimeFormatter.getString("timeDuration").format(hourStr, minuteStr);
            } else if (days) {
                timeDuration = dayStr;
            } else if (hours) {
                timeDuration = hourStr;
            } else if (minutes) {
                timeDuration = minuteStr;
            }
        }

        return timeDuration;
    }

    /**
     * Construct a formatted date time range string using the specified start and end date time strings
     * @param {string} startTimeString
     * @param {string} endTimeString
     * @returns {string} date time range string
     */
    private static constructDateTimeRangeString(startTimeString: string, endTimeString: string): string {
        return DateTimeFormatter.getString("dateTimeRangeFormat").format(startTimeString, endTimeString);
    }

    /**
     * Returns the title for a 24Hr shift who's start time is the input parameter
     * Example: 8:00AM (24h)
     * @param startTime start time of the 24hr shift
     * @param dateTimeFormat date time format
     */
    public static get24HrShiftFormat(startTime: Moment, dateTimeFormat?: DateTimeFormatType) {
        if (!startTime) {
            return "";
        }

        return DateTimeFormatter._dateTimeStrings.get("shift24Format").format(
            DateTimeFormatter.getDateTimeAsString(startTime, dateTimeFormat ? dateTimeFormat : DateTimeFormatType.Time),
            DateTimeFormatter._commonStrings.get("24hLabel"));
    }

    /**
     * Format as abbreviated month + day: "Mar 10", "Jun 1".
     * @example getMonthAndDateForDisplay(march10) → "Mar 10"
     */
    public static getMonthAndDateForDisplay(date: DateTimeInput): string {
        return date ? DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Date_DateMonthShort) : "";
    }

    /**
     * Returns the specified localized date time resource string
     * @param {string} stringResourceName string resource name
     * @returns {string} localized string resource
     */
    private static getString(stringResourceName: string): string {
        if (DateTimeFormatter._dateTimeStrings) {
            return DateTimeFormatter._dateTimeStrings.get(stringResourceName);
        } else {
            throw Error("DateTimeFormatter._dateTimeStrings not initialized.");
        }
    }

    /**
     * Returns an array of localized weekday names starting from Sunday (index 0).
     * @param style "long" (Sunday), "short" (Sun), or "narrow" (S)
     */
    public static getWeekdayNames(style: "long" | "short" | "narrow"): string[] {
        const names: string[] = [];
        // Jan 4, 2015 is a Sunday
        const baseSunday = new Date(2015, 0, 4);
        const fmt = new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, { weekday: style });
        for (let i = 0; i < 7; i++) {
            const d = new Date(baseSunday);
            d.setDate(baseSunday.getDate() + i);
            names.push(fmt.format(d));
        }
        return names;
    }

    /**
     * Returns an array of 12 localized month names.
     * @param style "long" (January) or "short" (Jan)
     */
    public static getMonthNames(style: "long" | "short"): string[] {
        const names: string[] = [];
        const fmt = new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, { month: style });
        for (let i = 0; i < 12; i++) {
            names.push(fmt.format(new Date(2015, i, 1)));
        }
        return names;
    }

    /**
     * Formats a Date using its browser-local components (getFullYear/getMonth/getDate) without
     * timezone conversion. Used by DatePicker components where the Date's local components
     * represent the displayed value.
     */
    public static formatBrowserDate(date: Date): string {
        if (!date) {
            return "";
        }
        // Lazily build and cache the no-timezone numeric date formatter.
        if (!DateTimeFormatter._numericDateFmtNoTz) {
            DateTimeFormatter._numericDateFmtNoTz = new Intl.DateTimeFormat(DateTimeFormatter._currentLocale, {
                day: DateTimeFormatter._shortDateDayFormat,
                month: DateTimeFormatter._shortDateMonthFormat,
                year: "numeric"
            });
        }
        if (DateTimeFormatter._hasRegionalDateOverride) {
            return DateTimeFormatter._assembleShortDateWithYear(DateTimeFormatter._numericDateFmtNoTz.formatToParts(date));
        }
        return DateTimeFormatter._numericDateFmtNoTz.format(date);
    }

    /**
     * Parses a locale-formatted date string (e.g., "4/14/2026", "14.04.2026") into a Date using
     * browser-local components, without timezone conversion. The inverse of formatBrowserDate().
     * Used by DatePicker components to parse user-typed text input.
     * Returns null if the string cannot be parsed or the date is invalid.
     */
    public static parseBrowserDate(dateString: string): Date | null {
        if (!dateString || !dateString.trim()) {
            return null;
        }
        // _shortDateOrder and _shortDateSeparator are always populated at initialize() time
        // (either from OS regional settings or by probing the locale default).
        const order = DateTimeFormatter._shortDateOrder;
        if (!order) {
            return null;
        }
        const parts = dateString.split(DateTimeFormatter._shortDateSeparator);
        if (parts.length < 2) {
            return null;
        }
        const values: Record<string, number> = {};
        for (let i = 0; i < order.length && i < parts.length; i++) {
            values[order[i]] = parseInt(parts[i], 10);
        }
        const day = values.day;
        const month = values.month;
        let year = values.year;
        if (isNaN(day) || isNaN(month)) {
            return null;
        }
        if (isNaN(year)) {
            year = new Date().getFullYear();
        }
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
        }
        const result = new Date(year, month - 1, day);
        // Verify the date didn't overflow (e.g., Feb 30 → Mar 2)
        if (result.getFullYear() !== year || result.getMonth() !== month - 1 || result.getDate() !== day) {
            return null;
        }
        return result;
    }

    /** Extract native Date from DateTimeInput. */
    private static toNativeDate(dateTime: DateTimeInput): Date {
        return dateTime instanceof Date ? dateTime : dateTime.toDate();
    }

    /**
     * Resolve the timezone for a DateTimeInput.
     * If the input is a Moment with its own .tz() (set via moment.tz() or .tz(zone)),
     * use that timezone. Otherwise fall back to the app's team timezone.
     * This preserves the original behavior where callers could pass Moments in
     * arbitrary timezones (e.g., getUserFriendlyTimeSlot converting to "Etc/UTC").
     */
    private static resolveTimezone(dateTime: DateTimeInput): string | undefined {
        if (!(dateTime instanceof Date)) {
            const momentTz = (dateTime as Moment).tz();
            if (momentTz) {
                return momentTz;
            }
        }
        return TimeZoneUtils.displayTimezone || undefined;
    }

    /**
     * Formats a time string using Intl.DateTimeFormat.
     * For 12-hour locales with useShortFormat, omits minutes when the time falls on the hour :00
     * (e.g., "8 PM" instead of "8:00 PM"). For 24-hour locales, always includes minutes.
     */
    private static formatTime(dateTime: DateTimeInput, useShortFormat: boolean): string {
        if (!dateTime) {
            return "";
        }
        const date = DateTimeFormatter.toNativeDate(dateTime);
        if (!date) {
            return "";
        }

        const tz = DateTimeFormatter.resolveTimezone(dateTime);
        const is12Hour = DateTimeFormatter.isCurrentLocale12HourTime();
        const omitMinutes = useShortFormat && is12Hour && DateUtils.getMinute(date) === 0;

        const options: Intl.DateTimeFormatOptions = {
            ...(omitMinutes ? { hour: "numeric" } : { hour: "numeric", minute: "2-digit" }),
            ...DateTimeFormatter._regionalOverrideOptions,
            ...(tz ? { timeZone: tz } : {})
        };

        let result = date.toLocaleTimeString(DateTimeFormatter._currentLocale, options);
        return DateTimeFormatter.applyLeadingZeroPreference(result);
    }

    /**
     * Strips or preserves leading zero on the hour based on OS regional settings.
     * Some Intl implementations force zero-padding when hour12:false is set on a locale
     * that doesn't natively use 24-hour time. This corrects for that.
     */
    private static applyLeadingZeroPreference(formatted: string): string {
        if (DateTimeFormatter._hasRegionalTimeOverride && !DateTimeFormatter._useLeadingZeroHour) {
            // Strip locale-specific leading zero from hour, skipping any directionality marks.
            // Regex is pre-computed at init time to handle non-Latin digit locales (e.g., Arabic-Indic).
            return formatted.replace(DateTimeFormatter._leadingZeroRegex, "");
        }
        return formatted;
    }

    /**
     * Formats the hour-only portion of a time using Intl.DateTimeFormat.
     * Returns just the numeric hour (e.g., "8" for 12-hour, "14" for 24-hour), without minutes or AM/PM.
     */
    private static formatHourOnly(dateTime: DateTimeInput): string {
        if (!dateTime) {
            return "";
        }
        const date = DateTimeFormatter.toNativeDate(dateTime);
        if (!date) {
            return "";
        }

        const parts = DateTimeFormatter.getHourOnlyFormatter().formatToParts(date);
        const hourPart = parts.find(p => p.type === "hour");
        if (!hourPart) {
            return "";
        }
        // Strip leading zero unless user explicitly chose leading-zero format (hh/HH).
        // Some 24-hour locales like de-DE zero-pad even with hour: "numeric".
        return DateTimeFormatter._useLeadingZeroHour ? hourPart.value : hourPart.value.replace(DateTimeFormatter._leadingZeroRegex, "");
    }

    /**
     * Formats just the AM/PM meridiem portion of a time using Intl.DateTimeFormat.
     * Returns empty string for 24-hour locales.
     */
    private static formatMeridiemOnly(dateTime: DateTimeInput): string {
        if (!dateTime) {
            return "";
        }
        if (!DateTimeFormatter.isCurrentLocale12HourTime()) {
            return "";
        }
        const date = DateTimeFormatter.toNativeDate(dateTime);
        if (!date) {
            return "";
        }

        const parts = DateTimeFormatter.getMeridiemFormatter().formatToParts(date);
        const dayPeriodPart = parts.find(p => p.type === "dayPeriod");
        return dayPeriodPart ? dayPeriodPart.value : "";
    }

    /**
     * Formats a weekday name using Intl.DateTimeFormat.
     * For Date_DayNarrow, uses "short" weekday truncated to 2 chars because Intl "narrow"
     * gives single-char (e.g., "S" for both Saturday and Sunday). The 2-char truncation
     * matches the previous Moment "dd" format behavior. Note: substring(0, 2) may cut
     * grapheme clusters in some scripts (Thai, Indic) — acceptable for the supported
     * locale set which uses Latin/CJK scripts for weekday abbreviations.
     */
    private static formatWeekday(dateTime: DateTimeInput, formatType: DateTimeFormatType): string {
        const date = DateTimeFormatter.toNativeDate(dateTime);
        if (!date) {
            return "";
        }
        const tz = DateTimeFormatter.resolveTimezone(dateTime);
        const tzOpt = tz ? { timeZone: tz } : {};

        if (formatType === DateTimeFormatType.Date_DayNarrow) {
            const short = date.toLocaleDateString(DateTimeFormatter._currentLocale, { weekday: "short", ...tzOpt });
            return short.substring(0, 2);
        }

        const weekdayStyle = formatType === DateTimeFormatType.Date_Day ? "long" : "short";
        return date.toLocaleDateString(DateTimeFormatter._currentLocale, { weekday: weekdayStyle as "long" | "short", ...tzOpt });
    }

    /**
     * Construct a formatted date + time string by combining separate date and time formats.
     * @param {DateTimeInput} dateTime - date/time to format
     * @param {DateTimeFormatType} dateFormatType - format type for the date portion
     * @param {DateTimeFormatType} timeFormatType - format type for the time portion
     * @param {boolean} useComma (optional) - separate date and time by a comma
     * @returns {string} formatted date time string
     */
    private static constructDateTimeString(dateTime: DateTimeInput, dateFormatType: DateTimeFormatType, timeFormatType: DateTimeFormatType, useComma: boolean = false): string {
        const dateString: string = DateTimeFormatter.getDateTimeAsString(dateTime, dateFormatType);
        const timeString: string = DateTimeFormatter.getDateTimeAsString(dateTime, timeFormatType);
        const dateTimeFormatString: string = useComma ? DateTimeFormatter.getString("dateCommaTimeFormat") : DateTimeFormatter.getString("dateTimeFormat");
        return dateTimeFormatString.format(dateString, timeString);
    }
}